import { useCallback, useRef } from "react";
import useSWRInfinite from "swr/infinite";
import { useWorkspaces } from "@/hooks/useWorkspaces";
import { Thread } from "@/models/agent/thread";
import { AgentService } from "@/services/agent/agent.service";
import { defaultSWROption } from "@/services/api/api.service";

export const PAGE_SIZE = 20;

export const createThreadsInfiniteKeyLoader =
  (sessionCurrentWorkspaceId: string) => (pageIndex: number, previousPageData: Thread[] | null) => {
    if (previousPageData && previousPageData.length < PAGE_SIZE) return null;
    return ["agent", "threads", sessionCurrentWorkspaceId, pageIndex] as const;
  };

const getKey =
  (sessionCurrentWorkspaceId: string | undefined) => (pageIndex: number, previousPageData: Thread[] | null) => {
    if (!sessionCurrentWorkspaceId) return null;
    return createThreadsInfiniteKeyLoader(sessionCurrentWorkspaceId)(pageIndex, previousPageData);
  };

const fetcher = async ([, , workspaceId, pageIndex]: readonly [string, string, string, number]) => {
  return AgentService.getThreads(workspaceId, pageIndex * PAGE_SIZE, PAGE_SIZE);
};

export const useInfiniteThreads = () => {
  const { sessionCurrentWorkspaceId } = useWorkspaces();

  const { data, error, isLoading, isValidating, size, setSize, mutate } = useSWRInfinite(
    getKey(sessionCurrentWorkspaceId),
    fetcher,
    {
      ...defaultSWROption,
      revalidateFirstPage: false,
    },
  );

  const threads = data ? data.flat() : [];
  const isLoadingMore = !isLoading && isValidating && size > 1;
  const hasMore = data ? (data[data.length - 1]?.length ?? 0) >= PAGE_SIZE : false;

  const observer = useRef<IntersectionObserver>();

  const observerRef = useCallback((node: HTMLDivElement | null) => {
    if (observer.current) observer.current.disconnect();

    observer.current = new IntersectionObserver(
      (entries) => {
        if (entries[0].isIntersecting && hasMore && !isLoadingMore) {
          setSize((prev) => prev + 1);
        }
      },
      { threshold: 0.1, rootMargin: "400px" },
    );

    if (node) observer.current.observe(node);
  }, [hasMore, isLoadingMore, setSize]);

  const refresh = useCallback(async () => {
    await mutate();
  }, [mutate]);

  return {
    threads,
    isLoading,
    isLoadingMore,
    error,
    hasMore,
    refresh,
    observerRef,
  };
};
